import type {
	ContractCostBreakDto,
	ContractPriceBreakDto,
} from '@supply-mover-2-0/shared';

export type ContractPriceBreak = Pick<
	ContractPriceBreakDto,
	'minimumQuantity' | 'price'
>;

/** Map of itemId → array of price breaks, as returned by getContractPricesForShopItems */
export type ContractPriceMap = Record<number, Array<ContractPriceBreak>>;

export type ContractCostBreak = Pick<
	ContractCostBreakDto,
	'minimumQuantity' | 'acquisitionCost' | 'sellCost'
>;

/** Map of itemId → array of cost breaks, as returned by getContractCostsForShopItems */
export type ContractCostMap = Record<number, Array<ContractCostBreak>>;

/**
 * Find the applicable contract price for an item at a given quantity.
 * Extracts the breaks for the item from the map, sorts ascending by
 * minimumQuantity, and returns the highest qualifying tier's price.
 */
export function getApplicableContractPrice(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	quantity: number,
): number | null {
	const breaks = contractPrices?.[itemId];

	if (breaks == null || breaks.length === 0) {
		return null;
	}

	const sorted = [...breaks].sort(
		(a, b) => a.minimumQuantity - b.minimumQuantity,
	);
	let applicablePrice: number | null = null;

	for (const brk of sorted) {
		if (quantity >= brk.minimumQuantity) {
			applicablePrice = brk.price;
		}
	}

	return applicablePrice;
}

/**
 * Find the applicable contract cost break for an item at a given quantity.
 * Mirrors getApplicableContractPrice: sorts the item's breaks ascending by
 * minimumQuantity and returns the highest qualifying tier. Returns null when no
 * break qualifies (e.g. the lowest tier's minimumQuantity exceeds `quantity`),
 * which the caller treats as "inherit the item's own cost." Each break may carry
 * a null acquisitionCost or sellCost individually (that dimension is not
 * contract-overridden), so callers must null-check per dimension too.
 */
export function getApplicableContractCost(
	contractCosts: ContractCostMap | null | undefined,
	itemId: number,
	quantity: number,
): ContractCostBreak | null {
	const breaks = contractCosts?.[itemId];

	if (breaks == null || breaks.length === 0) {
		return null;
	}

	const sorted = [...breaks].sort(
		(a, b) => a.minimumQuantity - b.minimumQuantity,
	);
	let applicable: ContractCostBreak | null = null;

	for (const brk of sorted) {
		if (quantity >= brk.minimumQuantity) {
			applicable = brk;
		}
	}

	return applicable;
}

/**
 * Check if a price matches the applicable contract price for an item/quantity.
 * Uses tolerance-based comparison to handle floating point precision.
 */
export function isPriceFromContract(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	price: number,
	quantity: number,
): boolean {
	const contractPrice = getApplicableContractPrice(
		contractPrices,
		itemId,
		quantity,
	);

	return contractPrice != null && Math.abs(price - contractPrice) < 0.001;
}

/**
 * Get the effective display price for an item.
 * Returns the contract price if one applies, otherwise the list price.
 */
export function getEffectiveItemPrice(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	quantity: number,
	listPrice: number,
): number {
	return (
		getApplicableContractPrice(contractPrices, itemId, quantity) ?? listPrice
	);
}

export interface ResolveEffectiveMinimumPriceOptions {
	/**
	 * The item's own `minimumPrice` column. Decimal columns arrive as strings
	 * from the database, so both shapes are accepted. An empty string is treated
	 * as "not set", the same as null.
	 */
	itemMinimumPrice: string | number | null | undefined;
	/**
	 * Whether the `instance-feature-item.defaultMinimumPrice` setting applies to
	 * the user (or channel) the minimum is being resolved for. The caller
	 * resolves it, because which preference set applies depends on who is
	 * acting (see PreferencesService.isDefaultMinimumPriceEnabled).
	 */
	defaultMinimumPriceEnabled: boolean;
	/**
	 * The client's contract price breaks, as returned by
	 * getContractPricesForShopItems for that client (saved prices are the
	 * priority-0 contract, so they arrive through the same map).
	 */
	contractPrices: ContractPriceMap | null | undefined;
	itemId: number;
	quantity: number;
	/** The item's list price (`item.price`). */
	listPrice: string | number | null | undefined;
}

/**
 * Parse a decimal price column (strings from the database, or numbers) into a
 * finite number. Null, an empty string and anything unparsable are "not set"
 * and return null; 0 is a real value and is returned as 0.
 */
export const parseOptionalPrice = (
	value: string | number | null | undefined,
): number | null => {
	if (value == null) {
		return null;
	}

	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (value.trim() === '') {
		return null;
	}

	const parsed = parseFloat(value);

	return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The price a client pays for an item at a quantity, or null when the item
 * has no price for that client at that quantity (PD-16171).
 *
 * The applicable contract price (saved prices and quantity tiers included)
 * wins over the list price. A resolved 0, from the list price or a contract,
 * is a real price and stays 0; only "nothing resolves" is null. An item
 * priced only by a contract tier the quantity has not reached is unpriced at
 * that quantity.
 *
 * Every cart-write path decides "may this line be added" from this, so the
 * CRM, the storefront and the server agree on what counts as unpriced.
 */
export function resolveOrderablePrice(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	quantity: number,
	listPrice: string | number | null | undefined,
): number | null {
	return (
		getApplicableContractPrice(contractPrices, itemId, quantity) ??
		parseOptionalPrice(listPrice)
	);
}

/**
 * Whether the item resolves a price at ANY quantity for the client: a list
 * price (0 included) or at least one contract price break. The storefront
 * uses it to decide whether to offer Add at all; the server still checks the
 * quantity actually requested (PD-16171).
 */
export function hasAnyOrderablePrice(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	listPrice: string | number | null | undefined,
): boolean {
	if (parseOptionalPrice(listPrice) != null) {
		return true;
	}

	const breaks = contractPrices?.[itemId];

	return breaks != null && breaks.length > 0;
}

/**
 * The lowest quantity at which a contract tier prices the item, or null when
 * the item has no contract breaks. Lets a refusal say "priced from a quantity
 * of 10" instead of only "no price set" for a tier-only contract.
 */
export function getLowestContractTierQuantity(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
): number | null {
	const breaks = contractPrices?.[itemId];

	if (breaks == null || breaks.length === 0) {
		return null;
	}

	return Math.min(...breaks.map((brk) => brk.minimumQuantity));
}

/**
 * The pricing fields a CRM shop row carries (ClientShopListWrapper adds the
 * underscore ones). List-builder cells receive the row untyped; they read
 * these through this view.
 */
export interface ShopRowPricingFieldsModel {
	/** The qty-1 contract price folded over the list price by the wrapper. */
	price?: string | number | null;
	/** The item's own list price, kept before `price` was overridden. */
	_listPrice?: string | number | null;
	_contractPriceBreaks?: Array<ContractPriceBreak> | null;
}

/**
 * PD-16171 — the price a CRM shop row's item resolves at `quantity` for the
 * client (contract tier, else list price), or null when it has none, which
 * keeps the shop's Add disabled until the rep types a price ($0.00 included). The
 * contract breaks come from the row, else `fallbackBreaks` (the validation
 * manager's copy, loaded after the first render).
 */
export function resolveShopRowOrderablePrice(
	row: ShopRowPricingFieldsModel,
	itemId: number,
	quantity: number,
	fallbackBreaks?: Array<ContractPriceBreak> | null,
): number | null {
	const listPrice = row._listPrice !== undefined ? row._listPrice : row.price;

	return resolveOrderablePrice(
		{ [itemId]: row._contractPriceBreaks ?? fallbackBreaks ?? [] },
		itemId,
		quantity,
		listPrice,
	);
}

/**
 * The price a CRM cart line is written with when a rep may type a price
 * (PD-16171), or null when the line must be refused.
 *
 * - The item resolves a price: the rep's price wins when one was entered
 *   (any value, 0 included, as before), otherwise the resolved price.
 * - The item resolves no price: only a price the rep entered is accepted,
 *   and a deliberate $0.00 counts. No entry at all is refused, so an
 *   unpriced item never reaches the cart at a price nobody chose.
 *
 * The storefront never takes a customer-entered price and does not use this.
 */
export function resolveRepEnteredLinePrice(
	repPrice: number | null | undefined,
	resolvedPrice: number | null,
): number | null {
	const enteredPrice =
		repPrice != null && Number.isFinite(repPrice) ? repPrice : null;

	if (resolvedPrice != null) {
		return enteredPrice ?? resolvedPrice;
	}

	return enteredPrice != null && enteredPrice >= 0 ? enteredPrice : null;
}

/**
 * Resolve the minimum price that applies to an order line (PD-16173).
 *
 * Mirrors the legacy CRM's `_getItemMinimumPrice`:
 *  1. An item's own minimum price always wins, including an explicit 0. The
 *     setting never overrides a value someone deliberately stored.
 *  2. Otherwise, when the default-minimum-price setting is off, there is no
 *     minimum (null), which is the behavior every tenant without the setting
 *     keeps.
 *  3. Otherwise the minimum is the price the shop shows in its price column for
 *     this client, item and quantity: the applicable contract price (saved
 *     price or other contract, tier chosen by quantity), else the list price,
 *     else 0.
 *
 * The legacy CRM prefers a saved price over a cheaper contract under the
 * LOWEST_PRICE pricing rule, while Workd 2.0 shows the lowest. The owner chose
 * to follow the price Workd 2.0 displays, so the minimum always equals the
 * price the rep sees.
 */
export function resolveEffectiveMinimumPrice(
	options: ResolveEffectiveMinimumPriceOptions,
): number | null {
	const ownMinimum = parseOptionalPrice(options.itemMinimumPrice);

	if (ownMinimum != null) {
		return ownMinimum;
	}

	if (!options.defaultMinimumPriceEnabled) {
		return null;
	}

	const contractPrice = getApplicableContractPrice(
		options.contractPrices,
		options.itemId,
		options.quantity,
	);

	if (contractPrice != null) {
		return contractPrice;
	}

	return parseOptionalPrice(options.listPrice) ?? 0;
}
